// Helpers for the Home page: what needs attention (OA / interview deadlines on
// tracker cards), how to-dos are grouped, and the season funnel. Pure functions,
// tested on their own.

import { toStatus, type ApiItem, type CardDeadline } from "./applicationCards";
import { formatMinutes } from "./prep";
import {
  appliedRoleIds,
  applicationsFor,
  daysUntil,
  openRoles,
  type DreamCompany,
  type OpenRole,
} from "./dreamCompanies";
import { seedHistory, stageFromLabel, type StageEvent } from "./applicationHistory";

// --- Needs attention (docs/plans/DEADLINES_PLAN.md, frontend Phase 2) ------------

export type AttentionItem = {
  card: ApiItem;
  kind: "oa" | "interview";
  /** The deadline, when it's still ahead and matches the card's stage */
  deadline: CardDeadline | null;
  /** ms; when the OA arrived, for undated OAs */
  since: number;
};

/** Local yyyy-mm-dd of an ISO time (an all-day deadline's calendar day). */
export function localDay(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Whether a deadline hasn't passed: all-day ones last the whole day. */
function isAhead(deadline: CardDeadline, now: Date): boolean {
  if (deadline.allDay) return (daysUntil(localDay(deadline.at), now) ?? -1) >= 0;
  return Date.parse(deadline.at) > now.getTime();
}

/**
 * When a stage change really happened. A synced event's `at` is when Caramel
 * read the email (a first sync reads months of mail at once), so it's the
 * Gmail date of the email behind it; a seeded Applied is the card's date;
 * a change made by hand is when it was made.
 */
function eventTime(card: ApiItem, event: StageEvent): number {
  const email = event.message_id ? (card.emails || []).find((m) => m.message_id === event.message_id) : undefined;
  if (email) return Date.parse(email.date) || Date.parse(event.at) || 0;
  if (event.source === "seed") return Date.parse(card.date) || Date.parse(event.at) || 0;
  return Date.parse(event.at) || Date.parse(card.date) || 0;
}

/** When the card reached its current stage (last history event), else its date. */
function stageSince(card: ApiItem): number {
  const last = (card.history || [])[(card.history || []).length - 1];
  return last ? eventTime(card, last) : Date.parse(card.date) || 0;
}

/**
 * The date the tracker shows on a card: when it reached its current stage,
 * or its applied date while it's still at the stage it started in.
 */
export function lastUpdate(card: ApiItem): string {
  const history = card.history || [];
  if (history.length < 2) return card.date;
  const at = eventTime(card, history[history.length - 1]);
  return at ? new Date(at).toISOString() : card.date;
}

/**
 * When a card's OA was finished (ms), from an "assessment completed" email, or
 * null. Only for the card's current OA: one finished before it reached OA
 * again (a second OA) doesn't count. The card stays at OA until the company
 * replies; it just stops asking to be finished.
 */
export function oaDoneAt(card: ApiItem): number | null {
  if (toStatus(card.predicted_label) !== "oa") return null;
  const since = stageSince(card);
  const marked = card.oaCompletedAt ? Date.parse(card.oaCompletedAt) : 0;
  if (marked && marked >= since) return marked;
  // A completion email the sync filed as a plain OA email (before it knew
  // HackerRank's "Thanks for taking the … Assessment"): read its subject
  const seen = (card.emails ?? [])
    .filter((e) => COMPLETED_SUBJECT.test(e.subject) && ASSESSMENT_WORD.test(e.subject))
    .map((e) => Date.parse(e.date))
    .filter((at) => at && at >= since);
  return seen.length ? Math.min(...seen) : null;
}

// Subjects that say an assessment was finished (backend: oa_completion._POSITIVE)
const COMPLETED_SUBJECT =
  /^\s*completed\s*:|\bassessments?\s+(?:has\s+been\s+|was\s+)?completed\b|\bthanks?(?:\s+you)?\s+for\s+(?:completing|taking|submitting)\s+(?!(?:the\s+)?time\b)|\bsubmission\s+confirm/i;
const ASSESSMENT_WORD = /\b(?:assessments?|tests?|challenge|coding|hirevue|video\s+interview)\b/i;

/** "OA done Oct 16": the tooltip on a finished OA's "Done" chip (it takes the due date's place) */
export const oaDoneTitle = (at: number) => `OA done ${new Date(at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

/**
 * Every pending OA (dated or not) and every interview with a deadline ahead.
 * Deadlines first, soonest first; then undated OAs, oldest first. A deadline
 * only counts if its type matches the card's current stage.
 */
export function attentionItems(cards: ApiItem[], now = new Date()): AttentionItem[] {
  const items: AttentionItem[] = [];
  for (const card of cards) {
    const status = toStatus(card.predicted_label);
    if (status !== "oa" && status !== "interview") continue;
    // A finished OA isn't pending
    if (oaDoneAt(card)) continue;
    const deadline = stageDeadline(card, now);
    if (status === "interview" && !deadline) continue;
    items.push({ card, kind: status, deadline, since: stageSince(card) });
  }
  return items.sort((a, b) => {
    if (a.deadline && b.deadline) return Date.parse(a.deadline.at) - Date.parse(b.deadline.at);
    if (a.deadline || b.deadline) return a.deadline ? -1 : 1;
    return a.since - b.since;
  });
}

/** Whole days until a deadline (0 = today). */
export function deadlineDays(deadline: CardDeadline, now = new Date()): number {
  return daysUntil(localDay(deadline.at), now) ?? 0;
}

/** "today", "tomorrow", "Friday", "Oct 14", plus ", 2:00 PM" when the time is known. */
export function deadlineWhen(deadline: CardDeadline, now = new Date()): string {
  const at = new Date(deadline.at);
  const days = deadlineDays(deadline, now);
  const day =
    days === 0
      ? "today"
      : days === 1
      ? "tomorrow"
      : days < 7
      ? at.toLocaleDateString("en-US", { weekday: "long" })
      : at.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (deadline.allDay) return day;
  return `${day}, ${at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

/** Banner headline: "Stripe online assessment due Friday", "Stripe interview tomorrow, 2:00 PM". */
export function attentionTitle(item: AttentionItem, now = new Date()): string {
  const company = item.card.company && item.card.company !== "Unknown" ? item.card.company : "Your";
  const { deadline } = item;
  if (item.kind === "oa") {
    return deadline ? `${company} online assessment due ${deadlineWhen(deadline, now)}` : `${company} online assessment`;
  }
  // An interview email's deadline is when to book a slot by, not the interview itself
  if (deadline?.source === "email") return `Book your ${company} interview by ${deadlineWhen(deadline, now)}`;
  return `${company} interview ${deadline ? deadlineWhen(deadline, now) : ""}`.trim();
}

/**
 * "Due Friday", "Tomorrow, 2:00 PM", "Book by Oct 14": the chip on a tracker
 * card. It sits next to the stage tag, so it doesn't say OA / Interview again.
 */
export function deadlineChip(deadline: CardDeadline, now = new Date()): string {
  const when = deadlineWhen(deadline, now);
  if (deadline.type === "oa") return `Due ${when}`;
  if (deadline.source === "email") return `Book by ${when}`;
  return when.charAt(0).toUpperCase() + when.slice(1);
}

/** The card's deadline when it's for the card's current stage and still ahead. */
export function stageDeadline(card: ApiItem, now = new Date()): CardDeadline | null {
  const d = card.deadline;
  return d && d.type === toStatus(card.predicted_label) && isAhead(d, now) ? d : null;
}

// --- Setting a deadline by hand (the card's edit form) ---------------------------

/** All-day deadlines set by hand count until the end of that day */
const ALL_DAY_TIME = "23:59";

/**
 * What the edit form's deadline fields start with for a stage: the card's
 * deadline for that stage, as yyyy-mm-dd and HH:MM ("" for all-day). An
 * interview email's date is when to book by, not the interview, so the
 * interview field starts empty.
 */
export function deadlineFields(card: ApiItem, stage: string): { date: string; time: string } {
  const d = card.deadline;
  if (!d || d.type !== stage || (stage === "interview" && d.source === "email")) return { date: "", time: "" };
  const at = new Date(d.at);
  const pad = (n: number) => String(n).padStart(2, "0");
  return { date: localDay(d.at), time: d.allDay ? "" : `${pad(at.getHours())}:${pad(at.getMinutes())}` };
}

/** The deadline the edit form saves: a local date and optional time, set by hand. */
export function manualDeadline(stage: "oa" | "interview", date: string, time: string): CardDeadline {
  return {
    type: stage,
    at: new Date(`${date}T${time || ALL_DAY_TIME}:00`).toISOString(),
    allDay: !time,
    source: "manual",
  };
}


// --- To-dos ----------------------------------------------------------------------

export type Task = {
  id: string;
  title: string;
  priority?: "high" | "medium" | "low";
  /** yyyy-mm-dd */
  dueDate?: string;
  completed: boolean;
  createdAt: any;
  /** When it was ticked: null while saving; missing on ones ticked before this was kept */
  completedAt?: any;
  /** Set on to-dos Caramel writes (autoTodos); the id is its key */
  auto?: AutoTodoMeta;
  /** yyyy-mm-dd; until then it's due that day instead (stored on your own to-dos) */
  snoozedUntil?: string;
  /** Snoozed right now (set by applySnooze) */
  snoozed?: boolean;
  /** Caramel's to-dos: why it's here ("Applied Sep 22 · no reply in 15 days") */
  reason?: string;
};

// --- Snooze ----------------------------------------------------------------------
//
// A snoozed to-do isn't hidden: it's due on the snooze day, so it moves to
// This week or Later and comes back to Today that day. Its own due date is
// kept, and used again once the snooze day has passed.

/** The to-do as it shows today: due on its snooze day while that's still ahead. */
export function applySnooze(task: Task, snoozedUntil: string | undefined, now = new Date()): Task {
  if (task.completed || !snoozedUntil || (daysUntil(snoozedUntil, now) ?? 0) <= 0) return task;
  return { ...task, dueDate: snoozedUntil, snoozed: true };
}

/** Snooze choices: tomorrow, in 3 days, in a week ("Tomorrow", "Sat", "Next Wed"). */
export function snoozeOptions(now = new Date()): { label: string; date: string }[] {
  const day = (n: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + n);
  const weekday = (d: Date) => d.toLocaleDateString("en-US", { weekday: "short" });
  return [
    { label: "Tomorrow", date: localDay(day(1).toISOString()) },
    { label: weekday(day(3)), date: localDay(day(3).toISOString()) },
    { label: `Next ${weekday(day(7))}`, date: localDay(day(7).toISOString()) },
  ];
}

export type TaskTab = "today" | "week" | "later" | "done";

export const TASK_TABS: { id: TaskTab; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "week", label: "This week" },
  { id: "later", label: "Later" },
  { id: "done", label: "Done" },
];

/** Ticked today (or just now, still saving): it stays on its list, crossed out, until midnight */
export function tickedToday(task: Task, now = new Date()): boolean {
  if (!task.completed || task.completedAt === undefined) return false;
  if (task.completedAt === null) return true;
  return localDay(new Date(createdMs(task.completedAt)).toISOString()) === localDay(now.toISOString());
}

/**
 * Today: due today, overdue, or no due date (nothing says it can wait).
 * This week: due in the next 6 days. Later: further out.
 * Done: ticked before today.
 */
export function taskTab(task: Task, now = new Date()): TaskTab {
  if (task.completed && !tickedToday(task, now)) return "done";
  const days = daysUntil(task.dueDate, now);
  if (days === null || days <= 0) return "today";
  return days < 7 ? "week" : "later";
}

/** Today shows this many; the rest wait behind "Show more" */
export const TODAY_LIMIT = 3;

/** How many open to-dos Today shows: a new user's setup to-dos don't take the urgent ones' places */
export const todayLimit = (today: Task[]): number =>
  TODAY_LIMIT + today.filter((t) => t.auto?.kind === "setup" && !t.completed).length;

/** Ticked to-dos Today shows; the rest wait behind "Show more" with the open ones */
export const TICKED_LIMIT = 3;

/** The first `limit` open to-dos, and the latest TICKED_LIMIT ticked ones (so the row just ticked stays) */
export function withinLimit(tasks: Task[], limit: number): Task[] {
  const tickedAt = (t: Task) => (t.completedAt === null ? Infinity : createdMs(t.completedAt));
  const latest = new Set(
    tasks
      .filter((t) => t.completed)
      .sort((a, b) => tickedAt(b) - tickedAt(a))
      .slice(0, TICKED_LIMIT),
  );
  let open = 0;
  return tasks.filter((t) => (t.completed ? latest.has(t) : open++ < limit));
}

/**
 * A list in its order with ticked to-dos last, except ones ticked since
 * `openedAt` (the page was opened): those stay where they are, so a row
 * doesn't jump away from the click.
 */
export function tickedLast(tasks: Task[], openedAt: number): Task[] {
  const stays = (t: Task) => !t.completed || t.completedAt === null || createdMs(t.completedAt) >= openedAt;
  return [...tasks.filter(stays), ...tasks.filter((t) => !stays(t))];
}

/** Among to-dos due the same day (or both undated): interviews, then OAs, then yours, then the rest */
const KIND_RANK: Record<AutoTodoKind | "own", number> = { prep: 0, book: 1, oa: 2, daily: 3, own: 4, setup: 5, apply: 6, followup: 7 };

const createdMs = (v: any): number => (typeof v === "number" ? v : typeof v?.toMillis === "function" ? v.toMillis() : 0);

/**
 * Order within a tab, most urgent first: dated before undated, soonest (most
 * overdue) first, then by kind, then newest first.
 */
export function compareTasks(a: Task, b: Task): number {
  if (!!a.dueDate !== !!b.dueDate) return a.dueDate ? -1 : 1;
  if (a.dueDate && b.dueDate && a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
  const rank = KIND_RANK[a.auto?.kind ?? "own"] - KIND_RANK[b.auto?.kind ?? "own"];
  return rank || createdMs(b.createdAt) - createdMs(a.createdAt);
}

/** "Overdue", "Today", "Tomorrow", "Due Fri", "Due Oct 14"; "" without a due date. */
export function dueLabel(dueDate?: string, now = new Date()): string {
  const days = daysUntil(dueDate, now);
  if (days === null) return "";
  if (days < 0) return "Overdue";
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  const date = new Date(dueDate + "T00:00:00");
  return days < 7
    ? `Due ${date.toLocaleDateString("en-US", { weekday: "short" })}`
    : `Due ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

// --- Season summary --------------------------------------------------------------

export type FunnelStep = "applied" | "oa" | "interview" | "offer" | "rejected" | "ghosted";

/** The stages an application can end at (rejected or ghosted there). */
type LossStage = "submitted" | "oa" | "interview";

export type SeasonSummary = {
  /** Most common season on the cards ("Fall 2026"), "" if none is a real, current one */
  season: string;
  counts: Record<FunnelStep, number>;
  /** The stage that loses the most applications, or null with nothing to go on */
  slip: { title: string; body: string; fact: string } | null;
  /** Applications that heard anything back (rejections, OAs, interviews, offers) */
  replies: number;
  /** The weakest stage behind `slip`, for keepApplying's wording */
  weakest: { stage: LossStage; lost: number; settled: number; ghosted: number } | null;
};

/** Settled applications needed before a stage can be called the weak spot */
const MIN_SETTLED = 3;
/** Below this many applications the advice is to apply more, whatever the replies say */
export const APPLY_TARGET = 50;
/** Applications a week, until the user sets their own goal */
export const DEFAULT_WEEKLY_GOAL = 10;

/** Applications that have heard back (or been ghosted) before the box shows at all: no sad news on day one */
const MIN_FINISHED = 3;

/** "All 5 replies so far" / "3 of 5 replies so far": out of what's come back, not out of everything applied to */
const soFar = (lost: number, settled: number, what: string) =>
  `${lost === settled ? "All" : `${lost} of`} ${settled} ${what} so far`;

/** What happened at the weakest stage, and what to do about it (the weekly goal shows just the fact) */
const SLIP_TEXT: Record<LossStage, (lost: number, settled: number, ghosted: number) => { fact: string; tip: string }> = {
  // Ghosted ones never replied, so they're "outcomes", not "replies"
  submitted: (lost, settled, ghosted) => ({
    fact: ghosted
      ? `${soFar(lost, settled, "outcomes")} ended before an assessment.`
      : `${soFar(lost, settled, "replies")} were rejections before an assessment.`,
    tip: "Start with your resume and the roles you’re picking.",
  }),
  oa: (lost, settled) => ({
    fact: `${soFar(lost, settled, "assessments")} ended there.`,
    tip: "Prep for assessments first.",
  }),
  interview: (lost, settled) => ({
    fact: `${soFar(lost, settled, "interviews")} ended without an offer.`,
    tip: "Practice interviewing next.",
  }),
};

/**
 * The current cycle as a funnel: how many applications reached each stage
 * (from their history, so an interview that ended in a rejection still counts
 * as an interview), and how many are now rejected or ghosted.
 *
 * The slip is the stage that loses the largest share of the applications that
 * got there and finished it (moved on, or were rejected / ghosted there).
 * Applications still waiting at a stage don't count either way.
 */
// "Summer 2027", "fall '26" -> season and year (backend archives._SEASON_YEAR); or a bare year
const SEASON_YEAR = /\b(summer|fall|autumn|winter|spring)\W+'?((?:20)?\d{2})\b/i;
const BARE_YEAR = /^'?(20\d{2})$/;

/**
 * A card's season as a name for the header ("Summer 2027", "2027 recruiting"), or null.
 * The season field is read from emails, so it can hold anything ("Reformer",
 * "2,500"); only real seasons count, and only ones not over yet.
 */
export function seasonName(szn: string | undefined, now = new Date()): string | null {
  const text = (szn || "").trim();
  const match = SEASON_YEAR.exec(text);
  let year: number;
  let name: string;
  if (match) {
    year = Number(match[2].length === 2 ? `20${match[2]}` : match[2]);
    const season = match[1].toLowerCase() === "autumn" ? "Fall" : match[1][0].toUpperCase() + match[1].slice(1).toLowerCase();
    name = `${season} ${year}`;
  } else {
    const bare = BARE_YEAR.exec(text);
    if (!bare) return null;
    year = Number(bare[1]);
    // A bare year on its own reads like a stat; say what it is
    name = `${year} recruiting`;
  }
  return year >= now.getFullYear() ? name : null;
}

export function seasonSummary(cards: ApiItem[], now = new Date()): SeasonSummary {
  const counts: Record<FunnelStep, number> = { applied: cards.length, oa: 0, interview: 0, offer: 0, rejected: 0, ghosted: 0 };
  const lost: Record<LossStage, number> = { submitted: 0, oa: 0, interview: 0 };
  const passed: Record<LossStage, number> = { submitted: 0, oa: 0, interview: 0 };
  const ghostedAt: Record<LossStage, number> = { submitted: 0, oa: 0, interview: 0 };
  const seasons = new Map<string, number>();

  for (const card of cards) {
    const stages = new Set(seedHistory(card).map((e) => e.stage));
    const current = stageFromLabel(card.predicted_label) ?? "submitted";
    stages.add(current);
    const oa = stages.has("oa");
    const interviewed = stages.has("interview") || stages.has("offer");
    const offer = stages.has("offer");
    if (oa) counts.oa++;
    if (interviewed) counts.interview++;
    if (offer) counts.offer++;

    if (oa || interviewed) passed.submitted++;
    if (oa && interviewed) passed.oa++;
    if (offer) passed.interview++;
    if (current === "rejected" || current === "ghosted") {
      counts[current]++;
      // Where it ended: the furthest stage it reached
      const at: LossStage = interviewed ? "interview" : oa ? "oa" : "submitted";
      lost[at]++;
      if (current === "ghosted") ghostedAt[at]++;
    }

    const szn = seasonName(card.szn, now);
    if (szn) seasons.set(szn, (seasons.get(szn) ?? 0) + 1);
  }

  const season = Array.from(seasons.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";

  let slip: SeasonSummary["slip"] = null;
  let weakest: SeasonSummary["weakest"] = null;
  // Ghosted straight from Applied never replied; one that got an OA first did
  const replies = lost.submitted + passed.submitted - ghostedAt.submitted;
  // Nothing to say until a few have heard back
  if (counts.applied > 0 && lost.submitted + passed.submitted >= MIN_FINISHED) {
    let worst: { stage: LossStage; rate: number; settled: number } | null = null;
    for (const stage of ["submitted", "oa", "interview"] as LossStage[]) {
      const settled = lost[stage] + passed[stage];
      if (settled < MIN_SETTLED) continue;
      const rate = lost[stage] / settled;
      if (!worst || rate > worst.rate) worst = { stage, rate, settled };
    }
    if (worst && worst.rate > 0) {
      weakest = { stage: worst.stage, lost: lost[worst.stage], settled: worst.settled, ghosted: ghostedAt[worst.stage] };
      slip = {
        title: "Where it’s slipping",
        ...(({ fact, tip }) => ({ fact, body: `${fact} ${tip}` }))(
          SLIP_TEXT[worst.stage](lost[worst.stage], worst.settled, ghostedAt[worst.stage])
        ),
      };
    } else if (worst) {
      slip = { title: "Nothing slipping yet", body: "No rejections so far. Keep going.", fact: "No rejections so far." };
    }
  }

  return { season, counts, slip, replies, weakest };
}

// --- Auto to-dos -----------------------------------------------------------------
//
// To-dos Caramel writes from what's happening: a new role at a dream company,
// an OA, an interview, an application with no reply. They aren't stored: they're
// worked out from the cards and companies each time, so one goes away on its
// own once it no longer applies (the card moved on, the role closed, you applied).
// Only what the user did with one is saved, by key (users/{uid}/autoTodos/{key}).
// A key changes when there's something new to do (a new OA, a new interview
// date, a newer role), so a done or dismissed to-do comes back for it.

/** "prep" is getting ready for one interview; "daily" is the day's Interview Prep plan */
export type AutoTodoKind = "apply" | "oa" | "book" | "prep" | "daily" | "followup" | "setup";

/** Where a to-do goes: a page, or on Home the weekly goal or feedback dialog */
export type AutoPage = "tracker" | "dream" | "prep" | "roles" | "drafts" | "goal" | "feedback";

export type AutoTodoMeta = {
  kind: AutoTodoKind;
  /** The tracker card, or the dream company for "apply" */
  cardId?: string;
  companyId?: string;
  /** "setup" only: the page it shows a new user */
  page?: AutoPage;
};

/** What the user did with an auto to-do (users/{uid}/autoTodos/{key}). */
export type AutoTodoState = {
  /** Unset: still to do (it may only be hidden from the banner) */
  status?: "done" | "dismissed";
  /** Taken off the Home banner (older saves, no return day); it stays in the to-do list */
  bannerHidden?: boolean;
  /** yyyy-mm-dd the banner shows it again, see bannerReturnDay */
  bannerHiddenUntil?: string;
  /** yyyy-mm-dd, see applySnooze */
  snoozedUntil?: string;
  /** Follow-ups: yyyy-mm-dd it was first in the list, see limitFollowUps */
  shownOn?: string;
  /** The title when it was ticked, for the Done tab once it no longer applies */
  title: string;
  /** Cleared from the Done tab (kept, so it doesn't come back) */
  hidden?: boolean;
  at: any;
};

/** Days after an OA arrives that one without a date is due */
const OA_DUE_DAYS = 5;
/** OAs and interviews older than this with no date ahead are taken as done or dropped */
const STAGE_STALE_DAYS = 21;
/** Days without hearing anything before suggesting a follow-up, and the last day to suggest it */
const FOLLOW_UP_AFTER_DAYS = 14;
const FOLLOW_UP_UNTIL_DAYS = 30;
/** Days a follow-up stays in the list once shown; left alone, it goes */
export const FOLLOW_UP_SHOWN_DAYS = 3;

/** Whether a follow-up for an application last heard from at `contact` came due after the user joined (ms; 0: unknown) */
const dueSinceJoining = (contact: number, joinedAt: number) => contact + FOLLOW_UP_AFTER_DAYS * DAY_MS >= joinedAt;
/** A new dream-company role is worth a to-do for this long, and due this soon */
const APPLY_WINDOW_DAYS = 14;
const APPLY_DUE_DAYS = 7;

const DAY_MS = 86400000;

/** "Sep 22" */
const shortDate = (ms: number) => new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function addDays(ms: number, days: number): string {
  return localDay(new Date(ms + days * DAY_MS).toISOString());
}

/**
 * When a pending OA is due, by the same rule as its to-do: its deadline, or
 * OA_DUE_DAYS after it arrived. Null once it's stale (no date ahead and older
 * than STAGE_STALE_DAYS). Interview Prep plans around this date.
 */
export function oaDue(card: ApiItem, now = new Date()): { due: string; known: boolean } | null {
  const since = stageSince(card);
  const dated = card.deadline && card.deadline.type === "oa" ? card.deadline : null;
  if (!(dated && isAhead(dated, now)) && (now.getTime() - since) / DAY_MS > STAGE_STALE_DAYS) return null;
  return { due: dated ? localDay(dated.at) : addDays(since, OA_DUE_DAYS), known: Boolean(dated) };
}

const companyName = (card: ApiItem) =>
  card.company && card.company !== "Unknown" ? card.company : "";

/** "Pinterest", or "Pinterest (Data Intern)" when there's more than one card there */
function cardLabel(card: ApiItem, cards: ApiItem[]): string {
  const name = companyName(card);
  const role = card.role?.trim();
  const twins = cards.filter((c) => c.id !== card.id && c.company === card.company && toStatus(c.predicted_label) === toStatus(card.predicted_label));
  return twins.length && role ? `${name} (${role})` : name;
}

/** ms; the newest of the stage change and the emails on the card */
function lastContact(card: ApiItem, since: number): number {
  return (card.emails || []).reduce((latest, email) => Math.max(latest, Date.parse(email.date) || 0), since);
}

/**
 * Every auto to-do that applies right now, before the user's done / dismissed
 * state. `joinedAt` (ms): no follow-ups for silences older than the account,
 * so a first sync doesn't suggest them for every old application.
 */
export function autoTodos(cards: ApiItem[], companies: DreamCompany[], now = new Date(), joinedAt = 0): Task[] {
  const todos: Task[] = [];
  const nowMs = now.getTime();
  const daysSince = (ms: number) => (nowMs - ms) / DAY_MS;
  const add = (
    id: string,
    title: string,
    dueDate: string | undefined,
    since: number,
    auto: AutoTodoMeta,
    reason: string
  ) => todos.push({ id, title, dueDate, completed: false, createdAt: since, auto, reason });
  const days = (ms: number) => plural(Math.floor(daysSince(ms)), "day", "days");

  for (const card of cards) {
    const name = companyName(card);
    if (!name) continue;
    const label = cardLabel(card, cards);
    const status = toStatus(card.predicted_label);
    const since = stageSince(card);
    // The card's deadline when it's for its current stage; `d` when it's still ahead
    const dated = card.deadline && card.deadline.type === status ? card.deadline : null;
    const d = dated && isAhead(dated, now) ? dated : null;

    if (status === "oa") {
      // Finished: nothing to do until the company replies
      if (oaDoneAt(card)) continue;
      // Past its date it shows as overdue, until it's stale
      if (!d && daysSince(since) > STAGE_STALE_DAYS) continue;
      const why = !dated
        ? `no deadline given, so aiming for ${OA_DUE_DAYS} days`
        : dated.source === "manual"
          ? "due date you set"
          : null;
      add(
        `oa:${card.id}:${since}`,
        `Finish ${label} OA`,
        dated ? localDay(dated.at) : addDays(since, OA_DUE_DAYS),
        since,
        { kind: "oa", cardId: card.id },
        why ? `OA arrived ${shortDate(since)} · ${why}` : `OA arrived ${shortDate(since)}`
      );
    } else if (status === "interview") {
      // An interview email's date is when to book a slot by; a calendar invite
      // or one entered by hand is the interview itself
      if (d?.source === "email") {
        add(
          `book:${card.id}:${d.at}`,
          `Book your ${label} interview`,
          localDay(d.at),
          since,
          { kind: "book", cardId: card.id },
          `Interview email ${shortDate(since)} · pick a time by ${deadlineWhen(d, now)}`
        );
      } else if (d) {
        add(
          `prep:${card.id}:${d.at}`,
          `Prep for ${label} interview`,
          localDay(d.at),
          since,
          { kind: "prep", cardId: card.id },
          `Interview ${deadlineWhen(d, now)} · ${d.source === "invite" ? "from the calendar invite" : "date you set"}`
        );
      } else if (!dated && daysSince(since) <= STAGE_STALE_DAYS) {
        // No date yet (a passed one means it's booked or done)
        add(
          `prep:${card.id}:${since}`,
          `Prep for ${label} interview`,
          undefined,
          since,
          { kind: "prep", cardId: card.id },
          `Moved to interview ${shortDate(since)} · add the date on the card`
        );
      }
    } else if (status === "submitted") {
      // Counted from the latest contact: any email on the card ("we're still
      // reviewing") restarts the wait, not just a stage change. The key holds it,
      // so a dismissed follow-up can come back after a newer silence.
      const contact = lastContact(card, since);
      const waited = daysSince(contact);
      if (waited >= FOLLOW_UP_AFTER_DAYS && waited <= FOLLOW_UP_UNTIL_DAYS && dueSinceJoining(contact, joinedAt)) {
        // "Last heard" when an email came after the one that put it at Applied
        const heardSince = contact > since + DAY_MS;
        add(
          `followup:${card.id}:${contact}`,
          `Follow up with ${label}?`,
          undefined,
          contact,
          { kind: "followup", cardId: card.id },
          heardSince
            ? `Last heard ${shortDate(contact)} · nothing in ${days(contact)}`
            : `Applied ${shortDate(contact)} · no reply in ${days(contact)}`
        );
      }
    }
  }

  for (const company of companies) {
    const roles = openRoles(company);
    const applied = appliedRoleIds(roles, applicationsFor(company, cards));
    const fresh = roles
      .filter((role) => role.new && !applied.has(role.id))
      .map((role) => ({ role, at: Date.parse(role.firstSeenAt || "") || 0 }))
      .filter(({ at }) => at && daysSince(at) <= APPLY_WINDOW_DAYS)
      .sort((a, b) => b.at - a.at);
    if (!fresh.length) continue;
    const newest = fresh[0];
    const title =
      fresh.length === 1
        ? `Apply to ${company.name} ${roleTitle(newest.role)}`
        : `Apply to ${fresh.length} new roles at ${company.name}`;
    const oldest = fresh[fresh.length - 1];
    add(
      `apply:${company.id}:${newest.role.id}`,
      title,
      addDays(newest.at, APPLY_DUE_DAYS),
      newest.at,
      { kind: "apply", companyId: company.id },
      fresh.length === 1
        ? `Opened ${shortDate(newest.at)} · matches what you’re looking for`
        : `Opened since ${shortDate(oldest.at)} · they match what you’re looking for`
    );
  }

  return todos;
}

const roleTitle = (role: OpenRole) => role.title.trim();

/** Dream companies a new user is asked to add */
export const STARTER_DREAM_COMPANIES = 3;

/** A new user's tour of the app, in this order; opening one ticks it */
const EXPLORE_TODOS: { page: AutoPage; title: string; reason: string }[] = [
  { page: "roles", title: "Browse roles picked for you", reason: "Matched to what you’re looking for and updated all day" },
  { page: "drafts", title: "Save a job to apply to later", reason: "Save one from New roles and find it in Drafts" },
  { page: "tracker", title: "See your applications", reason: "Caramel moves each one along as replies come in" },
  { page: "goal", title: "Set your weekly application goal", reason: "Your progress shows in Your season" },
  { page: "prep", title: "Try Interview Prep", reason: "A daily plan for your OAs and interviews" },
  { page: "feedback", title: "Tell Nora one thing that’s confusing", reason: "It goes straight to Nora" },
];

/** The tour's to-dos ticked by opening them; dream companies, a saved job and the goal finish when they're done */
export const opensToDone = (task: Task): boolean =>
  !!task.auto?.page && !["dream", "drafts", "goal"].includes(task.auto.page);

/**
 * Setup to-dos for new users (users/{uid}.starterTodos, set by onboarding):
 * add dream companies until they follow three, and with `explore`
 * (users/{uid}.exploreTodos) a tour of the app. One key each, so dismissing holds.
 */
export function setupTodos(companies: DreamCompany[], enabled: boolean, explore = false): Task[] {
  const tour: Task[] = explore
    ? EXPLORE_TODOS.map(({ page, title, reason }) => ({
        id: `setup:${page}`,
        title,
        completed: false,
        createdAt: 0,
        auto: { kind: "setup", page },
        reason,
      }))
    : [];
  const missing = STARTER_DREAM_COMPANIES - companies.length;
  // On the tour it's ticked once they follow three (Home); before it, it just went away
  if (!enabled || (missing <= 0 && !explore)) return tour;
  const title = missing <= 0
    ? `Add ${STARTER_DREAM_COMPANIES} dream companies`
    : companies.length
    ? `Add ${missing} more dream ${missing === 1 ? "company" : "companies"}`
    : `Add ${STARTER_DREAM_COMPANIES} dream companies`;
  const reason = companies.length && missing > 0
    ? `You follow ${companies.length} of ${STARTER_DREAM_COMPANIES} · Caramel watches their job boards for you`
    : "Caramel watches their job boards and tells you when a role fits";
  const dream: Task = { id: "setup:dreamCompanies", title, completed: false, createdAt: 0, auto: { kind: "setup", page: "dream" }, reason };
  return tour.length ? [tour[0], dream, ...tour.slice(1)] : [dream];
}

/**
 * Today's Interview Prep as one to-do: "Today's prep · 1 h 5", ticked once
 * every item is done or set aside. One key a day, so ticking it on Home only
 * holds for that day. Nothing on a rest day or before setup.
 */
export function dailyPrepTodo(
  items: { id: string; title: string; minutes: number }[],
  status: (id: string) => "done" | "skipped" | "later" | undefined,
  today: string
): Task | null {
  if (!items.length) return null;
  const open = items.filter((i) => !status(i.id));
  const total = items.reduce((sum, i) => sum + i.minutes, 0);
  const left = open.reduce((sum, i) => sum + i.minutes, 0);
  const finished = items.length - open.length;
  const next = open[0] ?? items[0];
  const more = (open.length || items.length) - 1;
  return {
    id: `daily:${today}`,
    title: open.length && finished ? `Today’s prep · ${formatMinutes(left)} left` : `Today’s prep · ${formatMinutes(total)}`,
    dueDate: today,
    completed: open.length === 0,
    createdAt: 0,
    // Finished today's plan: it's only ever today's
    completedAt: open.length === 0 ? Date.parse(`${today}T12:00:00`) : undefined,
    auto: { kind: "daily" },
    reason: [
      more > 0 ? `${next.title} and ${more} more` : next.title,
      finished ? `${finished} of ${items.length} done` : null,
    ]
      .filter(Boolean)
      .join(" · "),
  };
}

/** Whether an auto to-do has a page to open (one that no longer applies may not). */
export const autoTarget = (task: Task): AutoPage | null =>
  !task.auto
    ? null
    : task.auto.page
      ? task.auto.page
    : task.auto.kind === "daily"
      ? "prep"
      : task.auto.kind === "setup" || task.auto.companyId
      ? "dream"
      : task.auto.cardId
        ? "tracker"
        : null;

/**
 * Auto to-dos with the user's state applied: dismissed ones are gone, done
 * ones are ticked. Ones done earlier that no longer apply stay in Done (from
 * the saved title) until cleared.
 */
export function withAutoState(todos: Task[], state: Record<string, AutoTodoState>, now = new Date()): Task[] {
  const live = new Set(todos.map((t) => t.id));
  const out: Task[] = [];
  for (const todo of todos) {
    const s = state[todo.id];
    if (!s?.status) out.push(applySnooze(todo, s?.snoozedUntil, now));
    else if (s.status === "done" && !s.hidden) out.push({ ...todo, completed: true, completedAt: s.at });
  }
  for (const [key, s] of Object.entries(state)) {
    if (live.has(key) || s.status !== "done" || s.hidden) continue;
    out.push({ id: key, title: s.title, completed: true, createdAt: s.at, completedAt: s.at, auto: { kind: key.split(":")[0] as AutoTodoKind } });
  }
  return out;
}

/** Open follow-ups shown at once, so they never pile up */
export const FOLLOW_UP_LIMIT = 1;

/**
 * Follow-ups worth showing: one left alone FOLLOW_UP_SHOWN_DAYS after it was
 * first shown (`shownOn`, which Home saves) is gone, and only FOLLOW_UP_LIMIT
 * are open at once: the one already showing, else the newest silence (the
 * likeliest to still get a reply). Ticking one off, dismissing it or letting
 * it go lets the next in.
 */
export function limitFollowUps(tasks: Task[], state: Record<string, AutoTodoState> = {}, now = new Date()): Task[] {
  const shownOn = (t: Task) => state[t.id]?.shownOn;
  // Snoozed ones wait their turn without taking a slot
  const open = tasks.filter((t) => t.auto?.kind === "followup" && !t.completed && !t.snoozed);
  const expired = open.filter((t) => (daysUntil(shownOn(t), now) ?? 0) <= -FOLLOW_UP_SHOWN_DAYS);
  const waiting = open
    .filter((t) => !expired.includes(t))
    .sort((a, b) => Number(Boolean(shownOn(b))) - Number(Boolean(shownOn(a))) || createdMs(b.createdAt) - createdMs(a.createdAt));
  const dropped = new Set([...expired, ...waiting.slice(FOLLOW_UP_LIMIT)].map((t) => t.id));
  return dropped.size ? tasks.filter((t) => !dropped.has(t.id)) : tasks;
}

// --- The banner --------------------------------------------------------------------

/** Auto to-dos that can be on the banner: the ones about an OA or an interview */
const BANNER_KINDS: ReadonlySet<AutoTodoKind> = new Set<AutoTodoKind>(["oa", "book", "prep"]);

export type BannerEntry = {
  /** The OA / interview, for the countdown and headline */
  item: AttentionItem;
  /** Its to-do, which the banner takes out of the list while it's shown */
  todo: Task;
};

export type Banner = BannerEntry & {
  /** Other OAs / interviews waiting behind it, soonest first */
  rest: BannerEntry[];
  more: number;
};

/** The day an entry is due: its deadline, else its to-do's (an undated OA's aim) */
function entryDue(entry: BannerEntry): string | undefined {
  return entry.item.deadline ? localDay(entry.item.deadline.at) : entry.todo.dueDate;
}

/** Whole days until an entry is due (negative = overdue), null when there's no date at all. */
export function bannerDays(entry: BannerEntry, now = new Date()): number | null {
  const due = entryDue(entry);
  return due ? daysUntil(due, now) : null;
}

/** How loud the banner is: dark at 0–2 days (or overdue), caramel at 3–4, light with more time. */
export function bannerTone(entry: BannerEntry, now = new Date()): "urgent" | "soon" | "calm" {
  const days = bannerDays(entry, now);
  if (days === null || days >= 5) return "calm";
  return days >= 3 ? "soon" : "urgent";
}

/**
 * X on the banner hides it for now, not for good: the day it comes back.
 * 4+ days left: back at 3 days left. 2–3: back the day before. 1: back on
 * the day. Due today, overdue or undated: back tomorrow.
 */
export function bannerReturnDay(entry: BannerEntry, now = new Date()): string {
  const due = entryDue(entry);
  const days = due ? daysUntil(due, now) ?? 0 : 0;
  const before = days >= 4 ? 3 : days >= 2 ? 1 : 0;
  const ahead = days >= 1 ? days - before : 1;
  return localDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() + ahead).toISOString());
}

function hiddenFromBanner(entry: BannerEntry, saved: AutoTodoState | undefined, now: Date): boolean {
  if (saved?.bannerHiddenUntil) return (daysUntil(saved.bannerHiddenUntil, now) ?? 0) > 0;
  if (!saved?.bannerHidden) return false;
  // Hidden before X had a return day: back the day before it's due
  const due = entryDue(entry);
  return !due || (daysUntil(due, now) ?? 0) > 1;
}

/**
 * The most urgent OA or interview to-do (attentionItems' order) that's still
 * open and isn't hidden from the banner for now, with the others behind it; or null.
 */
export function pickBanner(
  cards: ApiItem[],
  todos: Task[],
  state: Record<string, AutoTodoState>,
  now = new Date()
): Banner | null {
  const open = todos.filter((t) => !t.completed && !t.snoozed && t.auto && BANNER_KINDS.has(t.auto.kind));
  const shown: BannerEntry[] = [];
  for (const item of attentionItems(cards, now)) {
    const todo = open.find((t) => t.auto?.cardId === item.card.id);
    if (todo && !hiddenFromBanner({ item, todo }, state[todo.id], now)) shown.push({ item, todo });
  }
  if (!shown.length) return null;
  const [first, ...rest] = shown;
  return { ...first, rest, more: rest.length };
}

// --- Since you were here ------------------------------------------------------------
//
// One line at the top of Home: what changed since the last visit. A visit is
// remembered on the user doc (homeVisit) so it carries across devices; coming
// back within VISIT_GAP_MS (a refresh, another tab) is the same visit.

/** users/{uid}.homeVisit: ms of this visit and of the one before */
export type HomeVisit = { at: number; previous?: number | null };

const VISIT_GAP_MS = 30 * 60 * 1000;

/** Opening Home: what to save, and the time the strip reports changes since (null on a first visit). */
export function nextVisit(stored: HomeVisit | null | undefined, now = Date.now()): { visit: HomeVisit; since: number | null } {
  if (!stored?.at) return { visit: { at: now, previous: null }, since: null };
  const previous = now - stored.at > VISIT_GAP_MS ? stored.at : stored.previous ?? null;
  return { visit: { at: now, previous }, since: previous };
}

/** "this morning", "yesterday", "Monday", "Oct 2" */
export function sinceLabel(since: number, now = new Date()): string {
  const then = new Date(since);
  const days = daysUntil(localDay(then.toISOString()), now) ?? 0;
  if (days === 0) return then.getHours() < 12 ? "this morning" : "earlier today";
  if (days === -1) return "yesterday";
  if (days > -7) return then.toLocaleDateString("en-US", { weekday: "long" });
  return then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "Wealthsimple", "Wealthsimple and Citadel", "3 companies" */
function names(list: string[], many = "companies"): string {
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.length} ${many}`;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * What changed since `since`, most exciting first: "Stripe made you an offer",
 * "Atlassian invited you to interview", "granola posted 1 new role",
 * "Wealthsimple rejected you", "2 follow-ups due". Only changes the sync made
 * (or the ghosting rule), not ones the user made by hand.
 */
export function sinceSummary(
  cards: ApiItem[],
  companies: DreamCompany[],
  since: number,
  now = new Date(),
  joinedAt = 0,
): string[] {
  const nowMs = now.getTime();
  const inWindow = (ms: number) => ms > since && ms <= nowMs;
  const by: Record<"offer" | "interview" | "oa" | "rejected" | "ghosted" | "new", string[]> = {
    offer: [],
    interview: [],
    oa: [],
    rejected: [],
    ghosted: [],
    new: [],
  };
  let followUps = 0;

  for (const card of cards) {
    const name = companyName(card) || "a company";
    const history = card.history || [];
    // The furthest it got in the window: an OA then an interview is one interview
    const recent = history.filter(
      (e) => e.source !== "manual" && e.source !== "seed" && inWindow(Date.parse(e.at) || 0)
    );
    const last = recent[recent.length - 1];
    if (last) {
      if (last.stage === "submitted") {
        if (history[0] === recent[0]) by.new.push(name);
      } else by[last.stage].push(name);
    }

    // A follow-up that became due in the window
    if (toStatus(card.predicted_label) === "submitted") {
      const contact = lastContact(card, stageSince(card));
      const due = contact + FOLLOW_UP_AFTER_DAYS * DAY_MS;
      const until = contact + FOLLOW_UP_UNTIL_DAYS * DAY_MS;
      if (companyName(card) && inWindow(due) && nowMs <= until && dueSinceJoining(contact, joinedAt)) followUps++;
    }
  }

  const roles = companies
    .map((company) => ({
      name: company.name,
      n: openRoles(company).filter((role) => role.new && inWindow(Date.parse(role.firstSeenAt || "") || 0)).length,
    }))
    .filter((c) => c.n > 0);
  const roleCount = roles.reduce((sum, c) => sum + c.n, 0);

  const out: string[] = [];
  if (by.offer.length) out.push(`${names(by.offer)} made you ${by.offer.length === 1 ? "an offer" : "offers"}`);
  if (by.interview.length) out.push(`${names(by.interview)} invited you to interview`);
  if (by.oa.length) out.push(`${names(by.oa)} sent you ${by.oa.length === 1 ? "an assessment" : "assessments"}`);
  if (roles.length === 1) out.push(`${roles[0].name} posted ${plural(roleCount, "new role", "new roles")}`);
  else if (roles.length > 1) out.push(`${plural(roleCount, "new role", "new roles")} at ${names(roles.map((c) => c.name), "dream companies")}`);
  if (by.rejected.length) out.push(`${names(by.rejected)} rejected you`);
  // As many as the to-do list shows at once
  const shownFollowUps = Math.min(followUps, FOLLOW_UP_LIMIT);
  if (shownFollowUps) out.push(plural(shownFollowUps, "follow-up due", "follow-ups due"));
  if (by.new.length) out.push(`${plural(by.new.length, "new application", "new applications")} tracked`);
  if (by.ghosted.length) out.push(`${plural(by.ghosted.length, "application", "applications")} went quiet`);
  return out;
}

// --- Weekly goal ---------------------------------------------------------------------
//
// Below APPLY_TARGET applications, Your season shows "This week 3 / 10 apps"
// instead of advice: a counter to fill. The goal is the user's (users/{uid}.weeklyGoal).

/** Monday 00:00, local time, of the week `now` is in */
export function weekStart(now = new Date()): Date {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start;
}

/** When the user applied: the card's date ("2026-10-08" read as a local day), else when it was added. */
export function appliedAt(card: ApiItem): number {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(card.date || "");
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])).getTime();
  const first = (card.history || [])[0];
  return Date.parse(card.date) || (first ? eventTime(card, first) : 0);
}

export type WeekGoal = {
  /** Applications since Monday */
  done: number;
  goal: number;
  hit: boolean;
  /** Applications left to APPLY_TARGET */
  toTarget: number;
  /** When the target is reached at this pace: "this week", "Nov" */
  by: string;
};

export function weeklyGoal(cards: ApiItem[], goal = DEFAULT_WEEKLY_GOAL, now = new Date()): WeekGoal {
  const start = weekStart(now).getTime();
  const done = cards.filter((card) => {
    const at = appliedAt(card);
    return at >= start && at <= now.getTime();
  }).length;
  const toTarget = Math.max(0, APPLY_TARGET - cards.length);
  // This week covers what's left of its goal; every later week covers a full goal
  const leftThisWeek = Math.max(0, goal - done);
  const laterWeeks = Math.ceil(Math.max(0, toTarget - leftThisWeek) / goal);
  const end = new Date(start + (laterWeeks + 1) * 7 * DAY_MS - 1);
  const by = laterWeeks === 0 ? "this week" : end.toLocaleDateString("en-US", { month: "short" });
  return { done, goal, hit: done >= goal, toTarget, by };
}

/**
 * The goal sentence, warmer the closer they get: "You’re 10 away from your goal
 * this week." at the start, "4 down, 6 to go this week." on the way,
 * "2 more and you’ve hit your goal." near the end.
 */
export function goalNudge(week: WeekGoal): string {
  const left = week.goal - week.done;
  // Close: the last 3 of a 10-goal (or the last 30%), never fewer than the last 2
  const close = left <= Math.max(2, Math.ceil(week.goal * 0.3));
  if (close) return left === 1 ? "1 more and you’ve hit your goal!" : `${left} more and you’ve hit your goal.`;
  if (week.done === 0) return `You’re ${left} away from your goal this week.`;
  return `${week.done} down, ${left} to go this week.`;
}

/**
 * The advice card under 50 applications, tied to the weekly goal:
 * "14 apps and 5 replies so far, all rejections before an assessment. More
 * applications is the fastest way to interviews right now. You're 3 away from
 * your goal this week." Headline "Goal hit" once the week's goal is reached.
 */
export function keepApplying(summary: SeasonSummary, week: WeekGoal): { title: string; body: string } {
  const { counts, replies, weakest, slip } = summary;
  const apps = plural(counts.applied, "app", "apps");
  let sofar: string;
  if (!replies) sofar = `${apps} so far, and replies are still coming in.`;
  else {
    const base = `${apps} and ${plural(replies, "reply", "replies")} so far`;
    // The pattern only once a few have heard back (slip is null before then)
    if (!slip) sofar = `${base}.`;
    else if (!weakest) sofar = `${base}, no rejections yet.`;
    else if (weakest.stage === "submitted" && !weakest.ghosted)
      sofar = `${base}, ${weakest.lost === weakest.settled ? "all" : `${weakest.lost} of them`} rejections before an assessment.`;
    else sofar = `${base}. ${slip.fact}`;
  }
  const goal = week.hit
    ? `You hit your goal this week. ${week.toTarget} more to ${APPLY_TARGET}.`
    : goalNudge(week);
  return {
    title: week.hit ? "Goal hit" : "Keep applying",
    body: `${sofar} More applications is the fastest way to interviews right now. ${goal}`,
  };
}

// --- Greeting ------------------------------------------------------------------------
//
// The headline at the top of Home, picked in layers: something happening in the
// search, then the day of the week, then the time of day. Each has a few
// wordings, picked by the date so it holds all day and changes tomorrow. Some
// leave the name out, where it reads better without one.

export type GreetingSignals = {
  /** An interview is on today */
  interviewToday?: boolean;
  /** An offer came in since the last visit */
  newOffer?: boolean;
  /** Finished onboarding in the last day */
  justJoined?: boolean;
  /** This week's application goal is hit */
  goalHit?: boolean;
  /** Days since the last visit, if known */
  awayDays?: number | null;
};

const GREETINGS = {
  interview: ["Interview day, {name}. You’ve got this.", "Big day today. You’ve got this."],
  offer: ["Congrats, {name}!", "An offer came in. Congrats!"],
  joined: ["Welcome to Caramel, {name}", "Welcome to Caramel"],
  goal: ["Goal hit. Nice week, {name}.", "Goal hit this week. Nice work."],
  back: ["Welcome back, {name}", "Good to see you again"],
  monday: ["New week, {name}", "Fresh start this week"],
  friday: ["Happy Friday, {name}", "Made it to Friday"],
  weekend: ["Weekend check-in, {name}", "Happy weekend"],
  morning: ["Good morning, {name}", "Morning, {name}", "Good morning"],
  afternoon: ["Good afternoon, {name}", "Hey, {name}", "Good afternoon"],
  evening: ["Good evening, {name}", "Evening, {name}", "Good evening"],
  late: ["Up late, {name}?", "Hi, night owl", "Still up?"],
} as const;

type GreetingKind = keyof typeof GREETINGS;

/** Back after this many days counts as "welcome back" */
const AWAY_DAYS = 7;

/** Fill in the name, or drop it ("Good morning, {name}" -> "Good morning") */
function fillName(text: string, name: string): string {
  if (name) return text.replace("{name}", name);
  return text.replace(/,? \{name\}/, "").replace("{name}", "");
}

/** A small stable hash, so the same day and kind always pick the same wording */
function dayHash(text: string): number {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) >>> 0;
  return h;
}

export function greetingKind(signals: GreetingSignals, now = new Date()): GreetingKind {
  if (signals.interviewToday) return "interview";
  if (signals.newOffer) return "offer";
  if (signals.justJoined) return "joined";
  if (signals.goalHit) return "goal";
  if ((signals.awayDays ?? 0) >= AWAY_DAYS) return "back";
  const hour = now.getHours();
  const day = now.getDay();
  // Day-of-week ones in the daytime; evenings and nights go by the clock
  if (hour >= 5 && hour < 17) {
    if (day === 1) return "monday";
    if (day === 5) return "friday";
    if (day === 0 || day === 6) return "weekend";
  }
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 22) return "evening";
  return "late";
}

/** The Home headline: "Good morning, Nora", "Interview day, Nora. You’ve got this.", "Hi, night owl". */
export function greeting(name: string, signals: GreetingSignals = {}, now = new Date()): string {
  const kind = greetingKind(signals, now);
  const options = GREETINGS[kind];
  const pick = options[dayHash(`${localDay(now.toISOString())}:${kind}`) % options.length];
  return fillName(pick, name.trim());
}
